import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import Navbar from './components/layout/Navbar';
import LanguageToggle from './components/ui/LanguageToggle';

// Public Pages
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SuscripcionPage from './pages/public/SuscripcionPage';
import CambiarPasswordPage from './pages/public/CambiarPasswordPage';
import TalleresPublicPage from './pages/public/TalleresPublicPage';
import CapacitacionesPage from './pages/public/CapacitacionesPage';
import GestoriaPage from './pages/public/GestoriaPage';
import GestoriaSeguimientoPage from './pages/public/GestoriaSeguimientoPage';
import VerificarEmailPage from './pages/public/VerificarEmailPage';
import AccionSolicitudPage from './pages/public/AccionSolicitudPage';
import PagoResultadoPage from './pages/public/PagoResultadoPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTalleres from './pages/admin/AdminTalleres';
import AdminFabricantes from './pages/admin/AdminFabricantes';
import AdminDocumentos from './pages/admin/AdminDocumentosV2';
import AdminPDFDashboard from './pages/admin/AdminPDFDashboard';
import AdminFinanzas from './pages/admin/AdminFinanzas';
import AdminCapacitaciones from './pages/admin/AdminCapacitaciones';
import AdminGestoria from './pages/admin/AdminGestoria';
import AdminSolicitudes from './pages/admin/AdminSolicitudes';
import AdminUsuarios from './pages/admin/AdminUsuarios';
import AdminImportar from './pages/admin/AdminImportar';
import AdminDescargas from './pages/admin/AdminDescargas';
import AdminSuscripciones from './pages/admin/AdminSuscripciones';
import AdminPaises from './pages/admin/AdminPaises';

// Taller Pages
import TallerDashboard from './pages/taller/TallerDashboard';
import TallerDocumentos from './pages/taller/TallerDocumentos';
import TallerUsuarios from './pages/taller/TallerUsuarios';
import TallerCarrito from './pages/taller/TallerCarrito';
import PagarCapacidadesPage from './pages/taller/PagarCapacidadesPage';

// Shared Pages
import NotificacionesPage from './pages/NotificacionesPage';

import './App.css';

// Protected Route Components
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

const AdminRoute = ({ children, requireSuperAdmin = false }) => {
  const { user, loading, isAdmin, isAdminFinanzas, isSuperAdmin } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }
  
  if (!user || !isAdmin()) {
    return <Navigate to="/" />;
  }
  
  // Si es admin_finanzas y la ruta requiere super_admin, redirigir a finanzas
  if (isAdminFinanzas() && requireSuperAdmin) {
    return <Navigate to="/admin/finanzas" />;
  }
  
  return children;
};

// Ruta exclusiva para super_admin y admin regular (no admin_finanzas)
const FullAdminRoute = ({ children }) => {
  const { user, loading, isAdmin, isAdminFinanzas } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }
  
  if (!user || !isAdmin()) {
    return <Navigate to="/" />;
  }
  
  // admin_finanzas solo puede acceder a finanzas
  if (isAdminFinanzas()) {
    return <Navigate to="/admin/finanzas" />;
  }
  
  return children;
};

const TallerRoute = ({ children }) => {
  const { user, loading, isTallerUser } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Cargando...</div>;
  }
  
  if (!user || !isTallerUser()) {
    return <Navigate to="/" />;
  }
  
  return children;
};

const AppContent = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rutas sin Navbar - Acciones desde Email y Pagos */}
        <Route path="/admin/accion-solicitud" element={<AccionSolicitudPage />} />
        <Route path="/verificar-email/:token" element={<VerificarEmailPage />} />
        <Route path="/pago/exito" element={<PagoResultadoPage />} />
        <Route path="/pago/error" element={<PagoResultadoPage />} />
        <Route path="/pago/pendiente" element={<PagoResultadoPage />} />
        
        {/* Rutas con Navbar */}
        <Route path="/*" element={
          <div className="min-h-screen bg-gray-50">
            <Navbar />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/suscripcion" element={<SuscripcionPage />} />
              <Route path="/cambiar-password" element={
                <ProtectedRoute><CambiarPasswordPage /></ProtectedRoute>
              } />
              <Route path="/talleres" element={<TalleresPublicPage />} />
              <Route path="/capacitaciones" element={<CapacitacionesPage />} />
              <Route path="/gestoria" element={<GestoriaPage />} />
              <Route path="/gestoria/seguimiento/:token" element={<GestoriaSeguimientoPage />} />
              
              {/* Admin Routes - Restringidas por rol */}
              <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/talleres" element={<FullAdminRoute><AdminTalleres /></FullAdminRoute>} />
              <Route path="/admin/fabricantes" element={<FullAdminRoute><AdminFabricantes /></FullAdminRoute>} />
              <Route path="/admin/paises" element={<FullAdminRoute><AdminPaises /></FullAdminRoute>} />
              <Route path="/admin/documentos" element={<FullAdminRoute><AdminDocumentos /></FullAdminRoute>} />
              <Route path="/admin/pdf-dashboard" element={<FullAdminRoute><AdminPDFDashboard /></FullAdminRoute>} />
              <Route path="/admin/finanzas" element={<AdminRoute><AdminFinanzas /></AdminRoute>} />
              <Route path="/admin/suscripciones" element={<FullAdminRoute><AdminSuscripciones /></FullAdminRoute>} />
              <Route path="/admin/solicitudes" element={<FullAdminRoute><AdminSolicitudes /></FullAdminRoute>} />
              <Route path="/admin/usuarios" element={<FullAdminRoute><AdminUsuarios /></FullAdminRoute>} />
              <Route path="/admin/capacitaciones" element={<FullAdminRoute><AdminCapacitaciones /></FullAdminRoute>} />
              <Route path="/admin/gestoria" element={<AdminRoute><AdminGestoria /></AdminRoute>} />
              <Route path="/admin/descargas" element={<FullAdminRoute><AdminDescargas /></FullAdminRoute>} />
              
              {/* Taller Routes */}
              <Route path="/panel" element={<TallerRoute><TallerDashboard /></TallerRoute>} />
              <Route path="/panel/documentos" element={<TallerRoute><TallerDocumentos /></TallerRoute>} />
              <Route path="/panel/usuarios" element={<TallerRoute><TallerUsuarios /></TallerRoute>} />
              <Route path="/panel/carrito" element={<TallerRoute><TallerCarrito /></TallerRoute>} />
              <Route path="/taller/pagar-capacidades/:solicitudId" element={<TallerRoute><PagarCapacidadesPage /></TallerRoute>} />
              
              {/* Shared Routes (require authentication) */}
              <Route path="/notificaciones" element={<ProtectedRoute><NotificacionesPage /></ProtectedRoute>} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
            <Toaster position="top-right" />
            <LanguageToggle />
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
